import { type I18nDefinition } from '@backmarket/nuxt-module-i18n/types'

export type FooterContentType =
  | 'payment-networks'
  | 'link'
  | 'link-internal'
  | 'link-external'
  | 'social-networks'
  | 'icons'
  | 'logos'

export type Logos = 'bcorp' | 'bbb' | 'customer-service-of-the-year'

export type FooterBaseContent<T> = {
  key: I18nDefinition
  type: FooterContentType
} & T

export type FooterLinkInternal = FooterBaseContent<{
  type: 'link-internal'
  name: string
  params?: Record<string, string>
  prefetch?: boolean
}>

export type FooterLink = FooterBaseContent<{
  url: string
  type: 'link' | 'link-external'
}>

export type FooterPaymentNetworks = FooterBaseContent<{
  type: 'payment-networks'
}>

export type FooterIcons = Omit<
  FooterBaseContent<{
    type: 'icons'
    url: string
    style: 'icon' | 'iconSmall'
    icons: Array<{
      src: string
      name: string
      width: number
      height: number
    }>
  }>,
  'key'
>

export type FooterSocials = Omit<
  FooterBaseContent<{
    type: 'social-networks'
    networks: {
      twitter?: string
      linkedin?: string
      facebook?: string
      instagram?: string
    }
  }>,
  'key'
>

export type FooterLogos = Omit<
  FooterBaseContent<{
    type: 'logos'
    logos: Logos[]
  }>,
  'key'
>

export type FooterContent =
  | FooterLink
  | FooterLinkInternal
  | FooterPaymentNetworks
  | FooterIcons
  | FooterSocials
  | FooterLogos

export function isFooterLink(content: FooterContent): content is FooterLink {
  return content.type === 'link' || content.type === 'link-external'
}

export function isFooterLinkInternal(
  content: FooterContent,
): content is FooterLinkInternal {
  return content.type === 'link-internal'
}

export function isFooterPaymentNetworks(
  content: FooterContent,
): content is FooterPaymentNetworks {
  return content.type === 'payment-networks'
}

export function isFooterIcons(content: FooterContent): content is FooterIcons {
  return content.type === 'icons'
}

export function isFooterSocials(
  content: FooterContent,
): content is FooterSocials {
  return content.type === 'social-networks'
}

export function isFooterLogos(content: FooterContent): content is FooterLogos {
  return content.type === 'logos'
}
